import { format } from 'date-fns';

// Naming and dating rules for the tenant plan package.
//
// The submitter is no longer asked for a plan set name or a review due date:
// the name comes from the project, and the review window is a fixed number of
// days from whenever the set was uploaded. Both live here so the submit dialog
// and the resubmission path in PlanReview cannot drift apart.

export const REVIEW_WINDOW_DAYS = 10;

// Local calendar date as YYYY-MM-DD. Deliberately not toISOString(), which is
// UTC and rolls an evening submission on to the next day for anyone west of it.
function toDateString(date) {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function submissionDate(from = new Date()) {
  return toDateString(new Date(from));
}

// A due date N days out, as the stored YYYY-MM-DD.
export function dueDateInDays(days, from = new Date()) {
  const due = new Date(from);
  due.setDate(due.getDate() + days);
  return toDateString(due);
}

export function reviewDueDate(from = new Date()) {
  return dueDateInDays(REVIEW_WINDOW_DAYS, from);
}

// A stored date column is a plain YYYY-MM-DD with no time zone. new Date() on
// one reads it as UTC midnight, which displays as the day before anywhere west
// of Greenwich — so build the local date from its parts instead.
export function parseDateOnly(value) {
  if (!value) return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!parts) return new Date(value);
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}

// Overdue means the due day has ended, so compare whole days: a set due today
// is not late. Comparing a stored date against the current instant makes it
// late from the previous afternoon, because the stored date parses as UTC.
export function isPastDue(value, from = new Date()) {
  const due = parseDateOnly(value);
  if (!due) return false;
  const today = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  return due < today;
}

// What the submission is called everywhere it is listed — the plans tab, the
// dashboard, the reports and the review emails all read plan_set_name.
export function planPackageName(project) {
  const parts = [project?.name, project?.suite_number]
    .map(part => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);
  return parts.length ? `${parts.join(' ')} — Tenant Plan Package` : 'Tenant Plan Package';
}

// Signage runs its own, shorter review clock. A sign package is one storefront
// elevation and its details, not a full set of drawings, so it is reviewed in a
// week rather than the ten days a plan package gets. Separate constant, not a
// shared one with two callers: changing one window must not move the other.
export const SIGNAGE_REVIEW_WINDOW_DAYS = 7;

export function signageReviewDueDate(from = new Date()) {
  return dueDateInDays(SIGNAGE_REVIEW_WINDOW_DAYS, from);
}

// When a submission was uploaded, to the minute. The first version carries the
// exact moment; submissions that predate the stamp fall back to the date
// column, which has no time in it.
export function submittedStamp(plan) {
  const uploaded = plan?.versions?.[0]?.uploaded_date;
  if (uploaded) return format(new Date(uploaded), 'MMM d, yyyy h:mm a');
  return plan?.submission_date ? format(parseDateOnly(plan.submission_date), 'MMM d, yyyy') : '';
}
